import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Award, Sparkles, Filter, Download, Search, Users, Building2,
  Calendar, ShieldCheck, Star, Loader2, ExternalLink, X, Trophy,
  ChevronDown, Info, Settings2, Plus, Pencil, Trash2, Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { formatDate } from '../../lib/dateUtils';
import RRNominatePage from './RRNominatePage';
import './rr-party.css';
import { emojiRain, playApplause, bigCelebration } from './party';
import WinnerPickerTab from './WinnerPickerTab';

/**
 * Admin/HR Rewards & Recognition console.
 * Provides:
 *   - Overview: per-category summary (nomination + nominee counts, shortlisted)
 *   - Full nominations table with filters
 *   - Shortlist / winner toggles
 *   - Excel export
 *   - Edition controls (extend nomination window)
 *
 * Nominators still can't peek at other nominators via this view — only HR/Admin.
 */
export default function AdminRRConsole({ api }) {
  const [tab, setTab] = useState('overview'); // overview | list | mine | settings
  const [edition, setEdition] = useState(null);
  const [summary, setSummary] = useState([]);
  const [noms, setNoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailNom, setDetailNom] = useState(null);
  const [filter, setFilter] = useState({ dept: 'all', award: 'all', q: '', shortlistedOnly: false });
  const [savingEdition, setSavingEdition] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [edRes, sumRes, listRes] = await Promise.all([
        api.get('/api/rr/edition/current'),
        api.get('/api/rr/admin/summary'),
        api.get('/api/rr/admin/nominations'),
      ]);
      setEdition(edRes.data);
      setSummary(sumRes.data.summary || []);
      setNoms(listRes.data.nominations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load R&R data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredNoms = useMemo(() => {
    return noms.filter(n => {
      if (filter.dept !== 'all' && n.award_department !== filter.dept) return false;
      if (filter.award !== 'all' && n.award_id !== filter.award) return false;
      if (filter.shortlistedOnly && !n.shortlisted) return false;
      if (filter.q) {
        const needle = filter.q.toLowerCase();
        const hay = [
          n.award_name, n.nominator_name, n.note,
          ...(n.nominees || []).map(x => x.name),
        ].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [noms, filter]);

  const departments = useMemo(() => {
    return ['all', ...Array.from(new Set(noms.map(n => n.award_department).filter(Boolean)))];
  }, [noms]);

  const awards = useMemo(() => {
    const map = new Map();
    noms.forEach(n => { if (!map.has(n.award_id)) map.set(n.award_id, n.award_name); });
    return [{ id: 'all', name: 'All awards' }, ...Array.from(map, ([id, name]) => ({ id, name }))];
  }, [noms]);

  const setFlag = async (nom, patch) => {
    try {
      const res = await api.patch(`/api/rr/admin/nominations/${nom._id}/flags`, patch);
      setNoms(prev => prev.map(x => x._id === nom._id ? { ...x, ...res.data } : x));
      if (detailNom?._id === nom._id) setDetailNom({ ...detailNom, ...res.data });
      toast.success('Updated');
      // Celebration when a nomination is flagged as Winner
      if (patch.winner === true && !nom.winner) {
        emojiRain(['🏆', '🎉', '🥇', '👑', '✨'], 3500);
        playApplause();
        bigCelebration();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/api/rr/admin/export', { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rr_nominations_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const saveEdition = async (patch) => {
    setSavingEdition(true);
    try {
      const res = await api.patch('/api/rr/admin/edition', patch);
      setEdition(res.data);
      toast.success('Edition updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingEdition(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]" data-testid="rr-admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="rr-party-scope" data-testid="rr-admin-console">
      <div className="rr-rainbow-bar" />
      <div className="p-3 md:p-8 max-w-[1400px]">
      {/* Hero */}
      <div className="mb-6 rounded-3xl bg-gradient-to-br from-amber-50 via-white to-rose-50 border border-amber-100/70 p-6 md:p-8 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100/70 text-amber-800 text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5" /> R&amp;R Admin Console
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 tracking-tight">
              {edition?.name}
            </h1>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-neutral-600">
              {edition?.event_date && <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-amber-600" />{formatDate(edition.event_date)}</span>}
              {edition?.closes_at && <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" />Closes {formatDate(edition.closes_at)}</span>}
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${edition?.is_open ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                {edition?.is_open ? '● Open' : '● Closed'}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExport} data-testid="rr-admin-export">
              <Download className="w-4 h-4 mr-1.5" /> Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'winners', label: '🏆 Winners' },
          { key: 'list', label: `Nominations · ${noms.length}` },
          { key: 'mine', label: 'My Nominations' },
          { key: 'categories', label: 'Categories & Mapping' },
          { key: 'settings', label: 'Settings' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
            }`}
            data-testid={`rr-admin-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewGrid summary={summary} onDrilldown={(awardId) => { setFilter(f => ({ ...f, award: awardId })); setTab('list'); }} />
      )}

      {tab === 'winners' && (
        <WinnerPickerTab api={api} />
      )}

      {tab === 'list' && (
        <NominationsTable
          noms={filteredNoms}
          departments={departments}
          awards={awards}
          filter={filter}
          setFilter={setFilter}
          onOpen={setDetailNom}
          onFlag={setFlag}
          totalCount={noms.length}
        />
      )}

      {tab === 'mine' && (
        <RRNominatePage api={api} portal="admin" />
      )}

      {tab === 'categories' && (
        <CategoriesManager api={api} onChanged={fetchAll} />
      )}

      {tab === 'settings' && (
        <EditionSettings edition={edition} onSave={saveEdition} saving={savingEdition} />
      )}

      {detailNom && (
        <NominationDetail
          nom={detailNom}
          onClose={() => setDetailNom(null)}
          onFlag={(patch) => setFlag(detailNom, patch)}
        />
      )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OverviewGrid({ summary, onDrilldown }) {
  if (summary.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="rr-admin-empty-overview">
        <Award className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
        <h3 className="text-base font-semibold text-neutral-800 mb-1">No nominations yet</h3>
        <p className="text-sm text-neutral-500">Once the team starts nominating, you&apos;ll see counts by award here.</p>
      </Card>
    );
  }

  const grouped = {};
  summary.forEach(s => {
    const d = s.award_department || 'Other';
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(s);
  });

  return (
    <div>
      {Object.entries(grouped).map(([dept, items]) => (
        <div key={dept} className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-neutral-400" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">{dept}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {items.map(s => (
              <button
                key={s.award_id}
                onClick={() => onDrilldown(s.award_id)}
                className="text-left p-4 bg-white border border-neutral-200 rounded-2xl hover:shadow-md hover:border-amber-300 transition-all"
                data-testid={`rr-admin-summary-${s.award_id}`}
              >
                <h3 className="text-sm font-bold text-neutral-900 mb-2 line-clamp-2">{s.award_name}</h3>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="text-neutral-500"><span className="font-bold text-neutral-900">{s.nomination_count}</span> nominations</span>
                  <span className="text-neutral-500"><span className="font-bold text-neutral-900">{s.unique_nominees}</span> nominees</span>
                  <span className="text-neutral-500"><span className="font-bold text-neutral-900">{s.unique_nominators}</span> nominators</span>
                </div>
                {s.shortlisted_count > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
                    <Star className="w-3 h-3" /> {s.shortlisted_count} shortlisted
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function NominationsTable({ noms, departments, awards, filter, setFilter, onOpen, onFlag, totalCount }) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search nominator, nominee, note…"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            className="pl-9 bg-white"
            data-testid="rr-admin-search"
          />
        </div>
        <select
          value={filter.dept}
          onChange={(e) => setFilter({ ...filter, dept: e.target.value })}
          className="h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm"
          data-testid="rr-admin-dept-filter"
        >
          {departments.map(d => <option key={d} value={d}>{d === 'all' ? 'All departments' : d}</option>)}
        </select>
        <select
          value={filter.award}
          onChange={(e) => setFilter({ ...filter, award: e.target.value })}
          className="h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm max-w-[240px]"
          data-testid="rr-admin-award-filter"
        >
          {awards.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={filter.shortlistedOnly}
            onChange={(e) => setFilter({ ...filter, shortlistedOnly: e.target.checked })}
            className="rounded"
            data-testid="rr-admin-shortlisted-toggle"
          />
          Shortlisted only
        </label>
        <span className="text-xs text-neutral-500 ml-auto">{noms.length} of {totalCount} shown</span>
      </div>

      {noms.length === 0 ? (
        <Card className="p-12 text-center text-neutral-500" data-testid="rr-admin-empty-list">
          No nominations match your filters.
        </Card>
      ) : (
        <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Award</th>
                  <th className="px-4 py-3 text-left font-semibold">Nominee</th>
                  <th className="px-4 py-3 text-left font-semibold">Nominator</th>
                  <th className="px-4 py-3 text-left font-semibold">Client</th>
                  <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                  <th className="px-4 py-3 text-center font-semibold w-24">Shortlist</th>
                  <th className="px-4 py-3 text-center font-semibold w-16">Winner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {noms.map(n => (
                  <tr key={n._id} className="hover:bg-neutral-50 cursor-pointer" onClick={() => onOpen(n)} data-testid={`rr-admin-row-${n._id}`}>
                    <td className="px-4 py-3">
                      <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">{n.award_department}</div>
                      <div className="font-medium text-neutral-900">{n.award_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {n.nominees.map(x => (
                          <span key={x.id} className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-xs">{x.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      <div>{n.nominator_name}</div>
                      {n.nominator_department && <div className="text-xs text-neutral-500">{n.nominator_department}</div>}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{n.client_name || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 text-neutral-500 text-xs">{formatDate(n.created_at)}</td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => onFlag(n, { shortlisted: !n.shortlisted })}
                        className={`p-1.5 rounded-full transition-colors ${n.shortlisted ? 'bg-amber-100 text-amber-700' : 'text-neutral-300 hover:text-amber-600'}`}
                        data-testid={`rr-admin-shortlist-${n._id}`}
                      >
                        <Star className={`w-4 h-4 ${n.shortlisted ? 'fill-amber-500' : ''}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => onFlag(n, { winner: !n.winner })}
                        className={`p-1.5 rounded-full transition-colors ${n.winner ? 'bg-yellow-100 text-yellow-700' : 'text-neutral-300 hover:text-yellow-600'}`}
                        data-testid={`rr-admin-winner-${n._id}`}
                      >
                        <Trophy className={`w-4 h-4 ${n.winner ? 'fill-yellow-400' : ''}`} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function NominationDetail({ nom, onClose, onFlag }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 py-8" onClick={onClose} data-testid="rr-admin-detail-backdrop">
      <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="rr-admin-detail">
        <div className="p-6 border-b border-neutral-100 flex items-start justify-between">
          <div className="flex-1">
            <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">{nom.award_department}</div>
            <h2 className="text-lg font-bold text-neutral-900">{nom.award_name}</h2>
            <p className="text-xs text-neutral-500 mt-1">Nominated by {nom.nominator_name}{nom.nominator_department ? ` · ${nom.nominator_department}` : ''} · {formatDate(nom.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="rr-admin-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Nominee</label>
            <div className="flex flex-wrap gap-1.5">
              {nom.nominees.map(n => (
                <span key={n.id} className="px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-sm">
                  <span className="font-semibold text-neutral-900">{n.name}</span>
                  <span className="text-neutral-500 text-xs ml-1">· {n.designation}</span>
                </span>
              ))}
            </div>
          </div>

          {nom.client_name && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Associated client</label>
              <div className="text-sm text-neutral-800">{nom.client_name}</div>
            </div>
          )}

          {nom.note && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">The case</label>
              <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">{nom.note}</p>
            </div>
          )}

          {nom.urls?.length > 0 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Supporting links</label>
              <div className="space-y-1.5">
                {nom.urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg text-xs text-blue-600 hover:underline">
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    <span className="truncate">{u}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {nom.attachments?.length > 0 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Attachments</label>
              <div className="grid grid-cols-2 gap-2">
                {nom.attachments.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg text-xs text-blue-600 hover:underline truncate">
                    <span className="truncate">{a.filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-neutral-100 bg-neutral-50/50 rounded-b-3xl flex items-center gap-2">
          <Button
            onClick={() => onFlag({ shortlisted: !nom.shortlisted })}
            variant={nom.shortlisted ? 'default' : 'outline'}
            className={nom.shortlisted ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            data-testid="rr-admin-detail-shortlist"
          >
            <Star className={`w-4 h-4 mr-1.5 ${nom.shortlisted ? 'fill-white' : ''}`} />
            {nom.shortlisted ? 'Shortlisted' : 'Add to shortlist'}
          </Button>
          <Button
            onClick={() => onFlag({ winner: !nom.winner })}
            variant={nom.winner ? 'default' : 'outline'}
            className={nom.winner ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
            data-testid="rr-admin-detail-winner"
          >
            <Trophy className={`w-4 h-4 mr-1.5 ${nom.winner ? 'fill-white' : ''}`} />
            {nom.winner ? 'Winner' : 'Mark as winner'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Convert a UTC ISO string to a `datetime-local` input value (YYYY-MM-DDTHH:MM)
// using the browser's LOCAL time (not UTC). Round to minute precision.
function toLocalDatetimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---------------------------------------------------------------------------
function EditionSettings({ edition, onSave, saving }) {
  const [closesAt, setClosesAt] = useState(toLocalDatetimeInput(edition?.closes_at));
  const [status, setStatus] = useState(edition?.status || 'open');
  const [winnersPublished, setWinnersPublished] = useState(!!edition?.winners_published);
  const [revealAt, setRevealAt] = useState(toLocalDatetimeInput(edition?.winners_reveal_at));

  return (
    <Card className="p-6 max-w-xl" data-testid="rr-admin-settings">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 className="w-4 h-4 text-neutral-500" />
        <h2 className="text-base font-bold text-neutral-900">Edition Settings</h2>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Nomination window closes at</label>
          <Input
            type="datetime-local"
            value={closesAt}
            onChange={(e) => setClosesAt(e.target.value)}
            data-testid="rr-admin-close-input"
          />
          <p className="text-xs text-neutral-500 mt-1">Your local time. Extending this reopens the window automatically.</p>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm"
            data-testid="rr-admin-status-select"
          >
            <option value="open">Open — anyone can nominate</option>
            <option value="closed">Closed — no more nominations</option>
            <option value="judging">Judging — closed but HR/Admin can flag winners</option>
            <option value="results_locked">Results locked — winners published</option>
          </select>
        </div>

        <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Nominators can only edit their own submissions while the window is open. HR &amp; Admin can always view, edit and delete.
        </div>

        {/* Winners announcement — controls what employees see under R&R tab */}
        <div className="border-t border-neutral-200 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-fuchsia-500" />
            <h3 className="text-sm font-bold text-neutral-900">Winners Announcement</h3>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Winners reveal at</label>
            <Input
              type="datetime-local"
              value={revealAt}
              onChange={(e) => setRevealAt(e.target.value)}
              data-testid="rr-admin-reveal-input"
            />
            <p className="text-xs text-neutral-500 mt-1">Employees see a countdown to this timestamp until winners are published.</p>
          </div>

          <label className="mt-3 flex items-center gap-3 p-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50 cursor-pointer" data-testid="rr-admin-publish-toggle-label">
            <input
              type="checkbox"
              checked={winnersPublished}
              onChange={(e) => setWinnersPublished(e.target.checked)}
              className="w-4 h-4 accent-fuchsia-600"
              data-testid="rr-admin-publish-toggle"
            />
            <div>
              <div className="text-sm font-semibold text-fuchsia-900">Publish winners to employees</div>
              <div className="text-[11px] text-fuchsia-700/80">
                {winnersPublished
                  ? 'Live — every employee now sees the winners page under R&R.'
                  : 'Off — employees see the "Winners coming soon" countdown.'}
              </div>
            </div>
          </label>
        </div>

        <Button
          onClick={() => onSave({
            status,
            closes_at: closesAt ? new Date(closesAt).toISOString() : undefined,
            winners_published: winnersPublished,
            winners_reveal_at: revealAt ? new Date(revealAt).toISOString() : undefined,
          })}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-700 text-white w-full"
          data-testid="rr-admin-save-settings"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Categories & Department Mapping
// ---------------------------------------------------------------------------
function CategoriesManager({ api, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [orgDepts, setOrgDepts] = useState([]);
  const [editing, setEditing] = useState(null); // category obj or 'new'

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, depRes] = await Promise.all([
        api.get('/api/rr/categories'),
        api.get('/api/rr/admin/org-departments'),
      ]);
      setCategories(catRes.data.categories || []);
      setOrgDepts(depRes.data.departments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (cat) => {
    if (!window.confirm(`Deactivate "${cat.name}"? Historical nominations stay linked.`)) return;
    try {
      await api.delete(`/api/rr/admin/categories/${cat._id}`);
      toast.success('Category deactivated');
      fetchData();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const grouped = useMemo(() => {
    const g = {};
    categories.forEach(c => { const d = c.department || 'Other'; (g[d] = g[d] || []).push(c); });
    return g;
  }, [categories]);

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-amber-600 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="rr-admin-categories-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-neutral-900">Categories &amp; Department Mapping</h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Map each award to one or more of your actual org departments — nominees will be filtered to that pool.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rr-admin-add-category-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Award
        </Button>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          <strong>Available org departments:</strong>{' '}
          {orgDepts.length === 0 ? <span>none found</span> : orgDepts.map(d => (
            <span key={d.name} className="inline-block bg-white border border-blue-200 rounded-full px-2 py-0.5 mr-1 mb-1 font-medium">
              {d.name} <span className="text-blue-400">· {d.employee_count}</span>
            </span>
          ))}
        </div>
      </div>

      {Object.entries(grouped).map(([dept, list]) => (
        <div key={dept}>
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-neutral-400" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500">{dept}</h3>
            <span className="text-xs text-neutral-400">· {list.length} awards</span>
          </div>
          <div className="space-y-2">
            {list.map(c => (
              <Card key={c._id} className="p-4 bg-white flex items-start justify-between gap-4" data-testid={`rr-admin-cat-row-${c._id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="text-sm font-bold text-neutral-900">{c.name}</h4>
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{c.bucket}</span>
                    {!c.is_active && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Inactive</span>}
                    {!c.department_restricted && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Open to all</span>}
                  </div>
                  <p className="text-xs text-neutral-500 line-clamp-2 mb-1.5">{c.description}</p>
                  <div className="text-[11px] text-neutral-600">
                    <span className="font-semibold">Eligible from: </span>
                    {c.department_restricted ? (
                      (c.eligible_departments || []).length > 0
                        ? c.eligible_departments.map(d => (
                            <span key={d} className="inline-block bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 mr-1 mb-0.5">{d}</span>
                          ))
                        : <span className="text-neutral-400 italic">— (not mapped)</span>
                    ) : <span className="text-emerald-700">all employees</span>}
                    <span className="ml-2 text-neutral-400">· {c.eligible_count} matches</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`rr-admin-cat-edit-${c._id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c)} className="text-red-600 hover:bg-red-50" data-testid={`rr-admin-cat-delete-${c._id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <CategoryFormDialog
          api={api}
          initial={editing === 'new' ? null : editing}
          orgDepts={orgDepts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function CategoryFormDialog({ api, initial, orgDepts, onClose, onSaved }) {
  const isEdit = !!initial?._id;
  const [name, setName] = useState(initial?.name || '');
  const [department, setDepartment] = useState(initial?.department || '');
  const [bucket, setBucket] = useState(initial?.bucket || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [deptRestricted, setDeptRestricted] = useState(initial?.department_restricted ?? true);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [eligibleDepts, setEligibleDepts] = useState(
    initial?.eligible_departments || (initial?.department ? [initial.department] : [])
  );
  const [saving, setSaving] = useState(false);

  const toggleDept = (name) => {
    setEligibleDepts(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };

  const submit = async () => {
    if (!name.trim()) return toast.error('Name is required');
    if (!department.trim()) return toast.error('Award group / department label is required');
    if (deptRestricted && eligibleDepts.length === 0) {
      return toast.error('Pick at least one org department for the eligibility pool');
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        department: department.trim(),
        bucket: bucket.trim(),
        description: description.trim(),
        department_restricted: deptRestricted,
        eligible_departments: eligibleDepts,
        is_active: isActive,
      };
      if (isEdit) {
        await api.patch(`/api/rr/admin/categories/${initial._id}`, payload);
        toast.success('Category updated');
      } else {
        await api.post('/api/rr/admin/categories', payload);
        toast.success('Category created');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 py-8" onClick={onClose} data-testid="rr-admin-cat-form-backdrop">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="rr-admin-cat-form">
        <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900">{isEdit ? 'Edit Award' : 'Add Award Category'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="rr-admin-cat-form-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Award name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Editor of the Year" data-testid="rr-cat-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Award group / label *</label>
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Strategy" data-testid="rr-cat-form-department" />
              <p className="text-[10px] text-neutral-400 mt-1">Grouping label shown on cards.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Bucket</label>
              <Input value={bucket} onChange={(e) => setBucket(e.target.value)} placeholder="e.g. Craft" data-testid="rr-cat-form-bucket" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Description</label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this award celebrate?" data-testid="rr-cat-form-description" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={deptRestricted} onChange={(e) => setDeptRestricted(e.target.checked)} data-testid="rr-cat-form-restricted" />
            <span>Restrict to specific departments</span>
          </label>
          {deptRestricted && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Eligible org departments *</label>
              <div className="border border-neutral-200 rounded-xl p-2 max-h-48 overflow-y-auto space-y-1">
                {orgDepts.length === 0 && (
                  <p className="text-xs text-neutral-500 p-2">No org departments found. Add employees with departments first.</p>
                )}
                {orgDepts.map(d => {
                  const on = eligibleDepts.includes(d.name);
                  return (
                    <label key={d.name} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-neutral-50 ${on ? 'bg-amber-50' : ''}`} data-testid={`rr-cat-form-dept-${d.name}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleDept(d.name)} className="rounded" />
                      <span className="flex-1 text-sm">{d.name}</span>
                      <span className="text-xs text-neutral-500">{d.employee_count} employee{d.employee_count !== 1 ? 's' : ''}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">Nominees must belong to any of these departments.</p>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="rr-cat-form-active" />
            <span>Active — visible for nominations</span>
          </label>
        </div>
        <div className="p-6 border-t border-neutral-100 bg-neutral-50/50 rounded-b-3xl flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="rr-cat-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rr-cat-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> {isEdit ? 'Save Changes' : 'Create Award'}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
